import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Card,
  Typography,
  CardContent,
  CardActions,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
const useStyles = makeStyles(theme => ({
  pageWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "100%",
  },
  modalContent: {
    padding: theme.spacing(2),
    backgroundColor: "#f9fafb",
  },
  formSection: {
    backgroundColor: "#f1f5f9",
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actionBar: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  smallButton: {
    borderRadius: 10,
    minWidth: 110,
    padding: theme.spacing(1, 2.5),
    fontSize: "0.85rem",
  },
  sectionPaper: {
    borderRadius: 16,
    padding: theme.spacing(3),
    background:
      "linear-gradient(135deg, rgba(243,246,255,0.9), rgba(255,255,255,0.95))",
    boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  helperText: {
    color: "#6b7280",
    fontSize: "0.9rem",
  },
  sectionCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
  },
  accentBar: {
    display: "inline-block",
    width: 4,
    height: 20,
    borderRadius: 4,
    backgroundColor: "#3b82f6",
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  fieldLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#374151",
    marginBottom: 4,
    display: "block",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  fieldHelper: {
    fontSize: "0.72rem",
    color: "#6b7280",
    marginTop: 4,
  },
  gridFixed4: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr 1fr" },
  },
  gridFixed3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr 1fr" },
  },
  gridFixed2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
  planCard: {
    position: "relative",
    borderRadius: 18,
    backgroundColor: "#fff",
    boxShadow: "0 18px 40px rgba(15,23,42,0.12)",
    overflow: "hidden",
  },
  planCardHeader: {
    width: "100%",
    height: 6,
    background:
      "linear-gradient(90deg, rgba(63,131,248,1) 0%, rgba(14,116,144,1) 100%)",
  },
  planCardContent: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  amountTag: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#1f2937",
  },
  statText: {
    color: "#475467",
    fontSize: "0.95rem",
  },
  chip: {
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
}));

const RECURRENCE_OPTIONS = [
  { value: "MENSAL", label: "Mensal" },
  { value: "TRIMESTRAL", label: "Trimestral" },
  { value: "SEMESTRAL", label: "Semestral" },
  { value: "ANUAL", label: "Anual" },
];

const defaultPlanValues = {
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    amount: 0,
    recurrence: "MENSAL",
    isPublic: true,
    // trial
    trial: false,
    trialDays: 0,
    // landing page
    description: '',
    buttonText: '',
    isFeatured: false,
    // canais
    useFacebook: true,
    useInstagram: true,
    useWhatsappOficial: false,
    useWhatsapp: true,
    useChat: true,
    useEmail: true,
    useMetaAds: true,
    useGoogleAds: true,
    useGoogleBusiness: true,
    useInternalChat: true,
    // módulos
    useCrm: true,
    useAutomacao: false,
    useCampaigns: false,
    useVendas: true,
    useMarketing: false,
    useFinanceiro: false,
    useIntegrations: false,
    useAgenda: true,
    useServicos: true,
    useProjetos: true,
    useTarefas: true,
    useSchedules: true,
    useExternalApi: false,
    // sub-flags legacy (mantidos por compatibilidade)
    useKanban: true,
    useOpenAi: false,
    useProdutos: true,
    useOrdemDeServico: true,
    // IA & NF
    aiCreditsLimit: 0,
    aiCreditsTrial: 0,
    nfLimit: 0,
    nfPricePerExtra: 0.00,
    nfBillingEnabled: false,
};

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()

    const [record, setRecord] = useState(defaultPlanValues);

    useEffect(() => {
        setRecord({
            ...defaultPlanValues,
            ...(initialValue || {})
        });
    }, [initialValue]);

    const simNao = [
      <MenuItem key="sim" value={true}>Sim</MenuItem>,
      <MenuItem key="nao" value={false}>Não</MenuItem>,
    ];

    const FieldSel = ({ name, children }) => (
      <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
        <Field as={Select} name={name}>{children || simNao}</Field>
      </FormControl>
    );

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={async (values, { resetForm }) => {
                await onSubmit(values);
                resetForm();
            }}
        >
            {({ values, setFieldValue }) => (
                <Form className={classes.fullWidth}>
                    <Box className={classes.formSection}>

  {/* ══ Seção 1: Plano ══ */}
  <Box className={classes.sectionCard}>
    <Box display="flex" alignItems="center" mb={2}>
      <span className={classes.accentBar} />
      <Box>
        <Typography variant="h6" style={{ lineHeight: 1.2, fontWeight: 700 }}>Plano</Typography>
        <Typography className={classes.helperText}>Limites, preço e visibilidade</Typography>
      </Box>
    </Box>

    <div className={classes.gridFixed4}>
      <Box>
        <span className={classes.fieldLabel}>Nome do Plano</span>
        <Field as={TextField} name="name" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Público</span>
        <FieldSel name="isPublic" />
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Recorrência</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Field as={Select} name="recurrence">
            {RECURRENCE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Field>
        </FormControl>
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Valor (R$)</span>
        <Field as={TextField} name="amount" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
      </Box>
    </div>

    <div className={classes.gridFixed4}>
      <Box>
        <span className={classes.fieldLabel}>Usuários</span>
        <Field as={TextField} name="users" type="number" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Conexões</span>
        <Field as={TextField} name="connections" type="number" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Departamentos</span>
        <Field as={TextField} name="queues" type="number" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
      </Box>
    </div>

    <div className={classes.gridFixed4}>
      <Box>
        <span className={classes.fieldLabel}>Período de Teste</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Field as={Select} name="trial">
            <MenuItem value={false}>Sem trial</MenuItem>
            <MenuItem value={true}>Com trial</MenuItem>
          </Field>
        </FormControl>
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Dias de Trial</span>
        <Field as={TextField} name="trialDays" type="number" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
        <span className={classes.fieldHelper}>0 = sem trial</span>
      </Box>
    </div>
  </Box>

  {/* ══ Seção 2: Landing Page ══ */}
  <Box className={classes.sectionCard}>
    <Box display="flex" alignItems="center" mb={2}>
      <span className={classes.accentBar} style={{ backgroundColor: "#0ea5e9" }} />
      <Box>
        <Typography variant="h6" style={{ lineHeight: 1.2, fontWeight: 700 }}>Apresentação na Landing Page</Typography>
        <Typography className={classes.helperText}>Como este plano aparece para os visitantes do site</Typography>
      </Box>
    </Box>

    <div className={classes.gridFixed3}>
      <Box>
        <span className={classes.fieldLabel}>Descrição (tagline)</span>
        <Field as={TextField} name="description" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
        <span className={classes.fieldHelper}>Aparece abaixo do nome do plano</span>
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Texto do botão</span>
        <Field as={TextField} name="buttonText" variant="outlined" size="small" fullWidth
          style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
        <span className={classes.fieldHelper}>Ex: "Começar agora →"</span>
      </Box>
      <Box>
        <span className={classes.fieldLabel}>Destaque "Mais Popular"</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Field as={Select} name="isFeatured">
            <MenuItem value={false}>Não</MenuItem>
            <MenuItem value={true}>Sim — exibir badge</MenuItem>
          </Field>
        </FormControl>
      </Box>
    </div>
  </Box>

  {/* ══ Seção 3: Canais ══ */}
  <Box className={classes.sectionCard}>
    <Box display="flex" alignItems="center" mb={2}>
      <span className={classes.accentBar} style={{ backgroundColor: "#10b981" }} />
      <Box>
        <Typography variant="h6" style={{ lineHeight: 1.2, fontWeight: 700 }}>Canais de Comunicação</Typography>
        <Typography className={classes.helperText}>Quais canais o cliente pode usar neste plano</Typography>
      </Box>
    </Box>
    <div className={classes.fieldGrid}>
      {[
        { name: "useFacebook",        label: "Facebook" },
        { name: "useInstagram",       label: "Instagram" },
        { name: "useWhatsappOficial", label: "WhatsApp Oficial" },
        { name: "useWhatsapp",        label: "WhatsApp Não Oficial" },
        { name: "useChat",            label: "Widget de Site" },
        { name: "useEmail",           label: "E-mail" },
        { name: "useMetaAds",         label: "Meta Ads" },
        { name: "useGoogleAds",       label: "Google Ads" },
        { name: "useGoogleBusiness",  label: "Google Meu Negócio" },
      ].map(({ name, label }) => (
        <Box key={name}>
          <span className={classes.fieldLabel}>{label}</span>
          <FieldSel name={name} />
        </Box>
      ))}
    </div>
  </Box>

  {/* ══ Seção 4: Módulos ══ */}
  <Box className={classes.sectionCard}>
    <Box display="flex" alignItems="center" mb={2}>
      <span className={classes.accentBar} style={{ backgroundColor: "#f59e0b" }} />
      <Box>
        <Typography variant="h6" style={{ lineHeight: 1.2, fontWeight: 700 }}>Módulos do Sistema</Typography>
        <Typography className={classes.helperText}>Funcionalidades disponíveis para o cliente</Typography>
      </Box>
    </Box>
    <div className={classes.fieldGrid}>
      {/* Módulos top-level com Field simples */}
      {[
        { name: "useCrm",          label: "CRM" },
        { name: "useAutomacao",    label: "Automação" },
        { name: "useCampaigns",    label: "Campanhas" },
        { name: "useVendas",       label: "Vendas" },
        { name: "useMarketing",    label: "Marketing" },
        { name: "useFinanceiro",   label: "Financeiro" },
        { name: "useIntegrations", label: "Integrações" },
        { name: "useSchedules",    label: "Lembretes" },
        { name: "useExternalApi",  label: "API Externa" },
      ].map(({ name, label }) => (
        <Box key={name}>
          <span className={classes.fieldLabel}>{label}</span>
          <FieldSel name={name} />
        </Box>
      ))}

      {/* Agenda — desabilitar desabilita Serviços */}
      <Box>
        <span className={classes.fieldLabel}>Agenda</span>
        <span className={classes.fieldHelper}>Desabilitar desabilita Serviços</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Select
            value={values.useAgenda}
            onChange={(e) => {
              const val = e.target.value;
              setFieldValue("useAgenda", val);
              if (!val) setFieldValue("useServicos", false);
            }}
          >
            <MenuItem value={true}>Sim</MenuItem>
            <MenuItem value={false}>Não</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Serviços — desabilitar desabilita Agenda */}
      <Box>
        <span className={classes.fieldLabel}>Serviços</span>
        <span className={classes.fieldHelper}>Desabilitar desabilita Agenda</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Select
            value={values.useServicos}
            onChange={(e) => {
              const val = e.target.value;
              setFieldValue("useServicos", val);
              if (!val) setFieldValue("useAgenda", false);
            }}
          >
            <MenuItem value={true}>Sim</MenuItem>
            <MenuItem value={false}>Não</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Projetos — desabilitar desabilita Tarefas */}
      <Box>
        <span className={classes.fieldLabel}>Projetos</span>
        <span className={classes.fieldHelper}>Desabilitar desabilita Tarefas</span>
        <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
          <Select
            value={values.useProjetos}
            onChange={(e) => {
              const val = e.target.value;
              setFieldValue("useProjetos", val);
              if (!val) setFieldValue("useTarefas", false);
            }}
          >
            <MenuItem value={true}>Sim</MenuItem>
            <MenuItem value={false}>Não</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tarefas */}
      <Box>
        <span className={classes.fieldLabel}>Tarefas</span>
        <FieldSel name="useTarefas" />
      </Box>
    </div>
  </Box>

  {/* ══ Seção 5: IA & Notas Fiscais (condicional) ══ */}
  {(values.useAutomacao || values.useFinanceiro) && (
  <Box className={classes.sectionCard} style={{ marginBottom: 0 }}>
    <Box display="flex" alignItems="center" mb={2}>
      <span className={classes.accentBar} style={{ backgroundColor: "#8b5cf6" }} />
      <Box>
        <Typography variant="h6" style={{ lineHeight: 1.2, fontWeight: 700 }}>IA & Notas Fiscais</Typography>
        <Typography className={classes.helperText}>Créditos de inteligência artificial e faturamento eletrônico</Typography>
      </Box>
    </Box>

    {values.useAutomacao && (
      <>
        <Typography style={{ fontSize: "0.8rem", fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
          INTELIGÊNCIA ARTIFICIAL
        </Typography>
        <div className={classes.gridFixed4} style={{ marginBottom: 16 }}>
          <Box>
            <span className={classes.fieldLabel}>Habilitar IA</span>
            <FieldSel name="useOpenAi" />
          </Box>
          <Box>
            <span className={classes.fieldLabel}>Créditos IA / mês</span>
            <Field as={TextField} name="aiCreditsLimit" type="number" variant="outlined" size="small" fullWidth
              style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
          </Box>
          <Box>
            <span className={classes.fieldLabel}>Créditos IA (trial)</span>
            <Field as={TextField} name="aiCreditsTrial" type="number" variant="outlined" size="small" fullWidth
              style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
          </Box>
        </div>
      </>
    )}

    {values.useFinanceiro && (
      <>
        <Typography style={{ fontSize: "0.8rem", fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
          NOTAS FISCAIS
        </Typography>
        <div className={classes.gridFixed4}>
          <Box>
            <span className={classes.fieldLabel}>Limite de NF / mês</span>
            <Field as={TextField} name="nfLimit" type="number" variant="outlined" size="small" fullWidth
              style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} />
          </Box>
          <Box>
            <span className={classes.fieldLabel}>Valor por NF extra</span>
            <Field as={TextField} name="nfPricePerExtra" variant="outlined" size="small" fullWidth
              style={{ backgroundColor: "#f8fafc", borderRadius: 8 }} inputProps={{ step: 0.01, min: 0 }} />
          </Box>
          <Box>
            <span className={classes.fieldLabel}>Cobrança por NF</span>
            <FormControl variant="outlined" size="small" fullWidth style={{ backgroundColor: "#f8fafc", borderRadius: 8 }}>
              <Field as={Select} name="nfBillingEnabled">
                <MenuItem value={true}>Ativada</MenuItem>
                <MenuItem value={false}>Desativada</MenuItem>
              </Field>
            </FormControl>
          </Box>
        </div>
      </>
    )}
  </Box>
  )}

  <Divider style={{ margin: "16px 0" }} />

  <Box className={classes.actionBar}>
    <ButtonWithSpinner
      startIcon={<CancelIcon />}
      className={classes.smallButton}
      loading={loading}
      onClick={() => onCancel()}
      variant="contained"
      style={{ color: "#4c1d95", backgroundColor: "#ede9fe", boxShadow: "none" }}
    >
      {i18n.t("plans.form.clear")}
    </ButtonWithSpinner>

    {record.id !== undefined && (
      <ButtonWithSpinner
        startIcon={<DeleteIcon />}
        className={classes.smallButton}
        loading={loading}
        onClick={() => onDelete(record)}
        variant="contained"
        style={{ color: "#b91c1c", backgroundColor: "#fee2e2", boxShadow: "none" }}
      >
        {i18n.t("plans.form.delete")}
      </ButtonWithSpinner>
    )}

    <ButtonWithSpinner
      startIcon={<SaveIcon />}
      className={classes.smallButton}
      loading={loading}
      type="submit"
      variant="contained"
      style={{ color: "#0f172a", backgroundColor: "#bae6fd", boxShadow: "none" }}
    >
      {i18n.t("plans.form.save")}
    </ButtonWithSpinner>
  </Box>
</Box>
                </Form>
            )}
        </Formik>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const classes = useStyles()

    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanban = (row) => {
        return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderOpenAi = (row) => {
        return row.useOpenAi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderIntegrations = (row) => {
        return row.useIntegrations === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    return (
<Grid container spacing={3}>
    {records.map((row) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
            <Card
                style={{
                    backgroundColor: "#ffffff",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    borderRadius: "10px",
                    padding: "20px",
                    margin: "10px",
                    transition: "transform 0.2s ease-in-out",
                    cursor: "pointer",
                       }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                <CardContent style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography
                        variant="subtitle2"
                        style={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}
                    >
                        {row.recurrence || "—"}
                    </Typography>
                    <Typography
                        variant="h5"
                        style={{ fontWeight: "bold", color: "#0f172a" }}
                    >
                        {row.name || "-"}
                    </Typography>
                    <Typography
                        variant="h4"
                        style={{ fontWeight: "bold", color: "#0ea5e9" }}
                    >
                        {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : "00,00"}
                    </Typography>
                    <Divider />
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t("plans.form.users")}: <strong>{row.users || "-"}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t("plans.form.public")}: <strong>{row.isPublic ? "Sim" : "Não"}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t("plans.form.connections")}: <strong>{row.connections || "-"}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Filas: <strong>{row.queues || "-"}</strong>
                    </Typography>
                </CardContent>
                <CardActions style={{ padding: "8px 16px", justifyContent: "flex-end" }}>
                    <IconButton
                        onClick={() => onSelect(row)}
                        aria-label="edit"
                        style={{
                        backgroundColor: "#3DB8FF",
                        borderRadius: "10px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "0.3s",
                       }}
                    >
                        <EditIcon style={{ color: "#fff" }}/>
                    </IconButton>
                </CardActions>
            </Card>
        </Grid>
    ))}
</Grid>
   )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        recurrence: "MENSAL",
        isPublic: true
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        console.log(data)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            setIsModalOpen(false)
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({ id: undefined, ...defaultPlanValues })
    }

    const toBoolean = (val, defaultVal = true) =>
        val === false || val === 0 || val === "false" ? false : val === true || val === 1 || val === "true" ? true : defaultVal

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            recurrence: data.recurrence || "MENSAL",
            isPublic: toBoolean(data.isPublic, true),
            trial:     toBoolean(data.trial, false),
            trialDays: data.trialDays || 0,
            description: data.description || '',
            buttonText:  data.buttonText || '',
            isFeatured:  toBoolean(data.isFeatured, false),
            // canais
            useFacebook:        toBoolean(data.useFacebook, true),
            useInstagram:       toBoolean(data.useInstagram, true),
            useWhatsappOficial: toBoolean(data.useWhatsappOficial, false),
            useWhatsapp:        toBoolean(data.useWhatsapp, true),
            useChat:            toBoolean(data.useChat, true),
            useEmail:           toBoolean(data.useEmail, true),
            useMetaAds:         toBoolean(data.useMetaAds, true),
            useGoogleAds:       toBoolean(data.useGoogleAds, true),
            useGoogleBusiness:  toBoolean(data.useGoogleBusiness, true),
            useInternalChat:    toBoolean(data.useInternalChat, true),
            // módulos top-level
            useCrm:          toBoolean(data.useCrm, true),
            useAutomacao:    toBoolean(data.useAutomacao, false),
            useCampaigns:    toBoolean(data.useCampaigns, false),
            useVendas:       toBoolean(data.useVendas, true),
            useMarketing:    toBoolean(data.useMarketing, false),
            useFinanceiro:   toBoolean(data.useFinanceiro, false),
            useIntegrations: toBoolean(data.useIntegrations, false),
            useAgenda:       toBoolean(data.useAgenda, true),
            useServicos:     toBoolean(data.useServicos, true),
            useProjetos:     toBoolean(data.useProjetos, true),
            useTarefas:      toBoolean(data.useTarefas, true),
            useSchedules:    toBoolean(data.useSchedules, true),
            useExternalApi:  toBoolean(data.useExternalApi, false),
            // sub-flags legacy
            useKanban:         toBoolean(data.useKanban, true),
            useOpenAi:         toBoolean(data.useOpenAi, false),
            useProdutos:       toBoolean(data.useProdutos, true),
            useOrdemDeServico: toBoolean(data.useOrdemDeServico, true),
            // IA & NF
            aiCreditsLimit:   data.aiCreditsLimit || 0,
            aiCreditsTrial:   data.aiCreditsTrial || 0,
            nfLimit:          data.nfLimit || 0,
            nfPricePerExtra:  data.nfPricePerExtra || 0,
            nfBillingEnabled: toBoolean(data.nfBillingEnabled, false),
        })
        setIsModalOpen(true)
    }

    const handleCreateNew = () => {
        handleCancel()
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        handleCancel()
    }

    return (
        <div className={classes.pageWrapper}>
            <Paper className={classes.sectionPaper} elevation={0}>
              <div className={classes.sectionHeader}>
                <div>
                  <Typography variant="h5">Planos</Typography>
                  <Typography className={classes.helperText}>
                    Gerencie os planos disponíveis no sistema.
                  </Typography>
                </div>
                <Button color="primary" variant="contained" onClick={handleCreateNew}>
                  Novo plano
                </Button>
              </div>
              <PlansManagerGrid
                records={records}
                onSelect={handleSelect}
              />
            </Paper>

            <Dialog
              open={isModalOpen}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {record.id ? "Editar plano" : "Novo plano"}
              </DialogTitle>
              <DialogContent dividers>
                <PlanManagerForm
                  initialValue={record}
                  onDelete={handleOpenDeleteDialog}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  loading={loading}
                />
              </DialogContent>
            </Dialog>

            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </div>
    )
}